import React, { useState } from "react";
import PropTypes from "prop-types";
import { makeStyles, shorthands, Spinner, mergeClasses } from "@fluentui/react-components";
import {
  Attach16Regular,
  ChevronDown16Regular,
  ChevronUp16Regular,
  Document16Regular,
  Open16Regular,
  Link16Regular,
} from "@fluentui/react-icons";
import PdfReferenceIcon from "./icons/PdfReferenceIcon";
import WordReferenceIcon from "./icons/WordReferenceIcon";

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    ...shorthands.padding("0px", "0px", "8px"),
    ...shorthands.gap("8px"),
    width: "100%",
    alignSelf: "stretch",
  },
  header: {
    fontFamily: "Segoe UI",
    fontStyle: "normal",
    fontWeight: 600,
    fontSize: "10px",
    lineHeight: "14px",
    letterSpacing: "0.04em",
    textTransform: "uppercase",
    color: "#666666",
  },
  docCard: {
    width: "100%",
    alignSelf: "stretch",
    ...shorthands.borderRadius("6px"),
    ...shorthands.border("1px", "solid", "#E0E0E0"),
    backgroundColor: "rgba(255, 255, 255, 0.6)",
  },
  docToggle: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    width: "100%",
    ...shorthands.gap("8px"),
    ...shorthands.padding("6px", "8px"),
    ...shorthands.borderRadius("6px"),
    border: "none",
    backgroundColor: "transparent",
    cursor: "pointer",
    textAlign: "left",
    minWidth: 0,
    fontFamily: "Segoe UI",
    ":hover": {
      backgroundColor: "rgba(0, 0, 0, 0.04)",
    },
  },
  docIcon: {
    width: "14px",
    height: "14px",
    flexShrink: 0,
  },
  docIconPdf: {
    width: "14px",
    height: "17px",
    flexShrink: 0,
    display: "block",
  },
  docIconWord: {
    width: "18px",
    height: "17px",
    flexShrink: 0,
    display: "block",
  },
  docIconDefault: {
    color: "#569797",
  },
  docTitle: {
    flexGrow: 1,
    flexShrink: 1,
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontSize: "12px",
    fontWeight: 600,
    lineHeight: "16px",
    color: "#242424",
  },
  chevron: {
    width: "14px",
    height: "14px",
    color: "#666666",
    flexShrink: 0,
  },
  docBody: {
    display: "flex",
    flexDirection: "column",
    ...shorthands.padding("0px", "8px", "8px"),
    ...shorthands.gap("8px"),
  },
  pageList: {
    display: "flex",
    flexDirection: "column",
    ...shorthands.gap("4px"),
    marginTop: "6px",
  },
  pageCard: {
    ...shorthands.borderRadius("4px"),
    ...shorthands.border("1px", "solid", "#EBEBEB"),
    backgroundColor: "rgba(255, 255, 255, 0.5)",
  },
  pageToggle: {
    display: "flex",
    flexDirection: "row",
    alignItems: "flex-start",
    width: "100%",
    ...shorthands.gap("8px"),
    ...shorthands.padding("4px", "8px"),
    ...shorthands.borderRadius("4px"),
    border: "none",
    backgroundColor: "transparent",
    cursor: "pointer",
    textAlign: "left",
    minWidth: 0,
    fontFamily: "Segoe UI",
    ":hover": {
      backgroundColor: "rgba(0, 0, 0, 0.04)",
    },
  },
  pageLabel: {
    flexShrink: 0,
    fontSize: "11px",
    fontWeight: 600,
    lineHeight: "14px",
    color: "#666666",
  },
  pageQuote: {
    flexGrow: 1,
    flexShrink: 1,
    minWidth: 0,
    fontSize: "12px",
    fontStyle: "italic",
    lineHeight: "16px",
    color: "rgba(36, 36, 36, 0.85)",
  },
  pageQuoteCollapsed: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  actions: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "flex-end",
    alignItems: "center",
    ...shorthands.gap("6px"),
  },
  actionButton: {
    display: "inline-flex",
    flexDirection: "row",
    alignItems: "center",
    ...shorthands.gap("4px"),
    ...shorthands.padding("4px", "8px"),
    ...shorthands.borderRadius("4px"),
    ...shorthands.border("1px", "solid", "#E0E0E0"),
    fontFamily: "Segoe UI",
    fontSize: "11px",
    lineHeight: "14px",
    color: "#0F6CBD",
    backgroundColor: "transparent",
    textDecoration: "none",
    cursor: "pointer",
    ":hover": {
      backgroundColor: "rgba(0, 0, 0, 0.04)",
      color: "#115EA3",
    },
    ":disabled": {
      cursor: "default",
      opacity: 0.6,
    },
  },
  actionIcon: {
    width: "12px",
    height: "12px",
  },
  copiedText: {
    fontFamily: "Segoe UI",
    fontSize: "11px",
    color: "#357C7C",
  },
  actionSpinner: {
    width: "16px",
    height: "16px",
  },
});

const normalizePageQuoteEntry = (entry, fallbackQuote = "") => {
  if (entry == null) return null;

  if (typeof entry === "object") {
    const page = entry.page ?? null;
    const quote = typeof entry.quote === "string" ? entry.quote : fallbackQuote;
    if ((page == null || page === "") && !quote) return null;
    return { page, quote };
  }

  if (entry === "" && !fallbackQuote) return null;
  return { page: entry, quote: fallbackQuote };
};

const sortPageQuotes = (items) =>
  [...items].sort((a, b) => {
    const pageA = Number(a.page);
    const pageB = Number(b.page);
    if (!Number.isNaN(pageA) && !Number.isNaN(pageB) && pageA !== pageB) {
      return pageA - pageB;
    }
    return 0;
  });

/** @returns {{ page: string|number|null, quote: string }[]} */
export const getPageQuotes = (link) => {
  // Current API: { quotes: [{ page, quote }, ...] }
  if (Array.isArray(link.quotes) && link.quotes.length > 0) {
    return sortPageQuotes(
      link.quotes.map((entry) => normalizePageQuoteEntry(entry)).filter(Boolean)
    );
  }

  // Legacy: pages array (sample UI format)
  if (Array.isArray(link.pages) && link.pages.length > 0) {
    return sortPageQuotes(
      link.pages.map((pq) => normalizePageQuoteEntry(pq)).filter(Boolean)
    );
  }

  const quote = typeof link.quote === "string" ? link.quote : "";
  const page = link.page;

  if (Array.isArray(page)) {
    if (page.length === 0) {
      return quote ? [{ page: null, quote }] : [];
    }
    return sortPageQuotes(
      page.map((entry) => normalizePageQuoteEntry(entry, quote)).filter(Boolean)
    );
  }

  if (page != null || quote) {
    return [{ page: page ?? null, quote }];
  }

  return [];
};

const formatPageLabel = (page) => {
  if (page == null || page === "") return null;
  return `Page ${page}`;
};

const PDF_RE = /\.pdf(?:\b|[?#&]|$)/i;
const DOCX_RE = /\.docx(?:\b|[?#&]|$)/i;

const decodeReferenceString = (value) => {
  if (value == null || value === "") return "";
  let str = String(value).trim();
  try {
    str = decodeURIComponent(str);
  } catch {
    // keep original when not URI-encoded
  }
  return str;
};

const extensionFromBasename = (value) => {
  const str = decodeReferenceString(value);
  if (!str) return "";

  const path = str.split("?")[0].split("#")[0];
  const basename = path.split(/[/\\]/).pop() || path;
  const dotIndex = basename.lastIndexOf(".");
  if (dotIndex <= 0 || dotIndex === basename.length - 1) return "";
  return basename.slice(dotIndex + 1).toLowerCase();
};

const extensionFromMimeOrType = (ref) => {
  const mime = decodeReferenceString(
    ref?.mime_type || ref?.content_type || ref?.contentType || ref?.type
  ).toLowerCase();
  if (mime.includes("pdf")) return "pdf";
  if (mime.includes("wordprocessingml") || mime.includes("msword") || mime.includes("docx")) {
    return "docx";
  }

  const fileType = decodeReferenceString(ref?.file_type || ref?.fileType || ref?.extension).toLowerCase();
  if (fileType === "pdf" || fileType === "docx") return fileType;
  return "";
};

/** @returns {"pdf"|"docx"|""} */
export const getReferenceExtension = (ref) => {
  const fromMime = extensionFromMimeOrType(ref);
  if (fromMime) return fromMime;

  const candidates = [
    ref?.filename,
    ref?.file_name,
    ref?.name,
    ref?.document,
    ref?.title,
    ref?.url,
    ref?.download_url,
  ];

  for (const candidate of candidates) {
    const str = decodeReferenceString(candidate);
    if (!str) continue;

    if (PDF_RE.test(str)) return "pdf";
    if (DOCX_RE.test(str)) return "docx";

    const ext = extensionFromBasename(str);
    if (ext === "pdf" || ext === "docx") return ext;
  }

  return "";
};

/** Display label for the reference row (extension stripped). */
export const getReferenceDisplayName = (ref) => {
  const raw = ref?.name || ref?.document || ref?.title || ref?.filename || ref?.file_name;
  if (!raw) return "Referenced document";

  const decoded = decodeReferenceString(raw);
  const withoutExt = decoded.replace(/\.(pdf|docx)$/i, "").trim();
  return withoutExt || decoded;
};

const ReferenceDocIcon = ({ reference, styles }) => {
  const ext = getReferenceExtension(reference);

  if (ext === "pdf") {
    return <PdfReferenceIcon className={styles.docIconPdf} />;
  }
  if (ext === "docx") {
    return <WordReferenceIcon className={styles.docIconWord} />;
  }

  const className = mergeClasses(styles.docIcon, styles.docIconDefault);
  return <Document16Regular className={className} aria-hidden />;
};

/** Avoid ResizeObserver loop when expand/collapse changes message list height. */
const deferLayoutUpdate = (fn) => {
  requestAnimationFrame(fn);
};

const ReferencesBlock = ({
  references,
  canAttach,
  onAttachReference,
  onShareReference,
  onPostReferenceUsage,
  attachingIndex,
  sharingIndex,
  copiedIndex,
  onAttach,
  onShare,
}) => {
  const styles = useStyles();
  const [openDoc, setOpenDoc] = useState(null);
  const [openPage, setOpenPage] = useState(null);

  if (!references?.length) return null;

  return (
    <div className={styles.container}>
      <p className={styles.header}>References</p>
      {references.map((ref, idx) => {
        const isDocOpen = openDoc === idx;
        const pageQuotes = getPageQuotes(ref);
        const showActions = canAttach && !ref.global_resource;

        return (
          <div key={idx} className={styles.docCard}>
            <button
              type="button"
              onClick={() => {
                if (!isDocOpen) {
                  onPostReferenceUsage?.("reference-expand", ref);
                }
                deferLayoutUpdate(() => setOpenDoc(isDocOpen ? null : idx));
              }}
              className={styles.docToggle}
              aria-expanded={isDocOpen}
            >
              <ReferenceDocIcon reference={ref} styles={styles} />
              <span className={styles.docTitle}>{getReferenceDisplayName(ref)}</span>
              {isDocOpen ? (
                <ChevronUp16Regular className={styles.chevron} />
              ) : (
                <ChevronDown16Regular className={styles.chevron} />
              )}
            </button>
            {isDocOpen && (
              <div className={styles.docBody}>
                {pageQuotes.length > 0 && (
                  <div className={styles.pageList}>
                    {pageQuotes.map((pq, pIdx) => {
                      const pageKey = `${idx}-${pIdx}`;
                      const isPageOpen = openPage === pageKey;
                      const pageLabel = formatPageLabel(pq.page);
                      const quoteText = pq.quote ? `"${pq.quote}"` : "";

                      return (
                        <div key={pageKey} className={styles.pageCard}>
                          <button
                            type="button"
                            onClick={() => {
                              if (!isPageOpen) {
                                onPostReferenceUsage?.("quote-expand", ref);
                              }
                              deferLayoutUpdate(() => setOpenPage(isPageOpen ? null : pageKey));
                            }}
                            className={styles.pageToggle}
                            aria-expanded={isPageOpen}
                          >
                            {pageLabel && <span className={styles.pageLabel}>{pageLabel}</span>}
                            {quoteText && (
                              <span
                                className={mergeClasses(
                                  styles.pageQuote,
                                  !isPageOpen && styles.pageQuoteCollapsed
                                )}
                              >
                                {quoteText}
                              </span>
                            )}
                            {quoteText &&
                              (isPageOpen ? (
                                <ChevronUp16Regular className={styles.chevron} />
                              ) : (
                                <ChevronDown16Regular className={styles.chevron} />
                              ))}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
                <div className={styles.actions}>
                  {ref.url && (
                    <a
                      href={ref.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.actionButton}
                      onClick={() => onPostReferenceUsage?.("reference-view", ref)}
                    >
                      <Open16Regular className={styles.actionIcon} />
                      View
                    </a>
                  )}
                  {showActions && onShareReference && (
                    sharingIndex === idx ? (
                      <Spinner className={styles.actionSpinner} size="tiny" />
                    ) : copiedIndex === idx ? (
                      <span className={styles.copiedText}>Copied!</span>
                    ) : (
                      <button type="button" className={styles.actionButton} onClick={() => onShare(ref, idx)}>
                        <Link16Regular className={styles.actionIcon} />
                        Copy Link
                      </button>
                    )
                  )}
                  {showActions && onAttachReference && (
                    attachingIndex === idx ? (
                      <Spinner className={styles.actionSpinner} size="tiny" />
                    ) : (
                      <button type="button" className={styles.actionButton} onClick={() => onAttach(ref, idx)}>
                        <Attach16Regular className={styles.actionIcon} />
                        Attach
                      </button>
                    )
                  )}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

ReferencesBlock.propTypes = {
  references: PropTypes.arrayOf(PropTypes.object).isRequired,
  canAttach: PropTypes.bool,
  onAttachReference: PropTypes.func,
  onShareReference: PropTypes.func,
  onPostReferenceUsage: PropTypes.func,
  attachingIndex: PropTypes.number,
  sharingIndex: PropTypes.number,
  copiedIndex: PropTypes.number,
  onAttach: PropTypes.func.isRequired,
  onShare: PropTypes.func.isRequired,
};

export default ReferencesBlock;
